/**
 * Namespaced localStorage wrapper for the CCNP Training Suite.
 *
 * All keys follow the pattern: ccnp:<namespace>:<key>
 *
 * Provides JSON serialization/deserialization, namespace isolation,
 * and bulk export/import per namespace.
 */

const PREFIX = 'ccnp:';

const storageService = {
  /**
   * Build the full storage key
   */
  _key(namespace, key) {
    return `${PREFIX}${namespace}:${key}`;
  },

  /**
   * Get a value from storage. Returns parsed JSON or defaultValue.
   */
  get(namespace, key, defaultValue = null) {
    try {
      const raw = localStorage.getItem(this._key(namespace, key));
      if (raw === null) return defaultValue;
      return JSON.parse(raw);
    } catch (e) {
      console.warn(`Storage read error [${namespace}:${key}]:`, e);
      return defaultValue;
    }
  },

  /**
   * Set a value in storage. Value is JSON-serialized.
   */
  set(namespace, key, value) {
    try {
      localStorage.setItem(this._key(namespace, key), JSON.stringify(value));
      return true;
    } catch (e) {
      console.error(`Storage write error [${namespace}:${key}]:`, e);
      return false;
    }
  },

  /**
   * Remove a key from storage.
   */
  remove(namespace, key) {
    localStorage.removeItem(this._key(namespace, key));
  },

  /**
   * Get all keys and values for a namespace.
   * Returns an object { key: value, ... }
   */
  getAll(namespace) {
    const prefix = `${PREFIX}${namespace}:`;
    const result = {};
    for (let i = 0; i < localStorage.length; i++) {
      const fullKey = localStorage.key(i);
      if (fullKey.startsWith(prefix)) {
        const shortKey = fullKey.slice(prefix.length);
        try {
          result[shortKey] = JSON.parse(localStorage.getItem(fullKey));
        } catch (e) {
          result[shortKey] = localStorage.getItem(fullKey);
        }
      }
    }
    return result;
  },

  /**
   * Export all data for a namespace as a JSON string (for backup/transfer).
   */
  exportNamespace(namespace) {
    const data = this.getAll(namespace);
    return JSON.stringify({
      _export: {
        app: namespace,
        exportedAt: new Date().toISOString(),
        version: 1,
      },
      data,
    }, null, 2);
  },

  /**
   * Import data for a namespace from a JSON string.
   * Overwrites existing data for matching keys.
   */
  importNamespace(namespace, jsonString) {
    try {
      const parsed = JSON.parse(jsonString);
      const data = parsed.data || parsed;
      for (const [key, value] of Object.entries(data)) {
        if (key.startsWith('_')) continue; // skip metadata keys
        this.set(namespace, key, value);
      }
      return { success: true, keysImported: Object.keys(data).filter(k => !k.startsWith('_')).length };
    } catch (e) {
      console.error('Import error:', e);
      return { success: false, error: e.message };
    }
  },

  /**
   * Clear all data for a namespace.
   */
  clearNamespace(namespace) {
    const prefix = `${PREFIX}${namespace}:`;
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    return keysToRemove.length;
  },

  /**
   * Check if an app's data has been seeded (first-load initialization).
   */
  isInitialized(appName) {
    return this.get('__init', appName, false);
  },

  /**
   * Mark an app as initialized after seeding.
   */
  markInitialized(appName) {
    this.set('__init', appName, true);
  },

  /**
   * Reset initialization flag (for "Reset to defaults").
   */
  resetInitialized(appName) {
    this.remove('__init', appName);
  },
};

export default storageService;
